import React, { useRef } from "react";
import { Linking, ScrollView, StyleSheet, Text, View } from "react-native";
import { Clock3, Globe2, Mail, MapPin, Navigation, Phone, UserRound } from "lucide-react-native";

import { BackButtonBackHome } from "../Universal/Buttons/BackButtonBackHomeModified";
import { useAppContext } from "../Universal/API/Context-API/AppContext";
import { webFont } from "../Universal/API/API";
import YMapsFullScreen from "../Universal/Yandex/Maps/YMapsFullScreen";
import { dealer } from "../WhereToBuy/Interfaces/Interface";
import { openNavigation } from "../WhereToBuy/FormMenuDealer/FormMenuDealer";
import PressableWrapp from "../Universal/Buttons/PressableReact/PressableWrapp";

interface ContactInfoScreenProps {
  navigation: any;
  title: string;
  contact: dealer;
  showBrandManager?: boolean;
  eyebrow?: string;
  subtitle?: string;
}

const createStyles = (scaleAll: number, isSmallVersion: boolean, isMediumVersion: boolean) =>
  StyleSheet.create({
    screen: {
      flex: 1,
      backgroundColor: "#FFFFFF",
    },
    scrollContent: {
      paddingTop: 12 * scaleAll,
      paddingBottom: 72 * scaleAll,
      paddingHorizontal: 16 * scaleAll,
      alignItems: "center",
    },
    shell: {
      width: "100%",
      maxWidth: 1480 * scaleAll,
      alignSelf: "center",
    },
    hero: {
      marginTop: 18 * scaleAll,
      paddingHorizontal: 4 * scaleAll,
      marginBottom: 14 * scaleAll,
    },
    eyebrow: {
      fontFamily: webFont("Inter"),
      fontSize: 12 * scaleAll,
      color: "#6B665F",
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: 0.7,
      marginBottom: 8 * scaleAll,
    },
    title: {
      fontFamily: webFont("Inter"),
      fontSize: 22 * scaleAll,
      lineHeight: 27 * scaleAll,
      color: "#111111",
      fontWeight: "700",
      marginBottom: 8 * scaleAll,
      maxWidth: 980 * scaleAll,
    },
    subtitle: {
      fontFamily: webFont("Inter"),
      fontSize: 14 * scaleAll,
      lineHeight: 21 * scaleAll,
      color: "#6F6A63",
      fontWeight: "500",
      maxWidth: 980 * scaleAll,
    },
    introWrap: {
      flexDirection: !isSmallVersion && !isMediumVersion ? "row" : "column",
      gap: 14 * scaleAll,
      marginTop: 10 * scaleAll,
    },
    mapCard: {
      flex: !isSmallVersion && !isMediumVersion ? 1.08 : undefined,
      borderRadius: 30 * scaleAll,
      backgroundColor: "#FBFBFA",
      borderWidth: 1,
      borderColor: "#ECE8E1",
      overflow: "hidden",
      minHeight: isSmallVersion ? 280 * scaleAll : isMediumVersion ? 420 * scaleAll : 560 * scaleAll,
    },
    mapInner: {
      width: "100%",
      height: "100%",
      minHeight: isSmallVersion ? 280 * scaleAll : isMediumVersion ? 420 * scaleAll : 560 * scaleAll,
    },
    infoCard: {
      flex: 0.92,
      borderRadius: 30 * scaleAll,
      backgroundColor: "#FFFFFF",
      borderWidth: 1,
      borderColor: "#ECE8E1",
      paddingHorizontal: 22 * scaleAll,
      paddingTop: 22 * scaleAll,
      paddingBottom: 22 * scaleAll,
    },
    infoBadge: {
      alignSelf: "flex-start",
      minHeight: 42 * scaleAll,
      borderRadius: 999,
      paddingHorizontal: 14 * scaleAll,
      paddingVertical: 7 * scaleAll,
      backgroundColor: "#111111",
      justifyContent: "center",
      marginBottom: 14 * scaleAll,
    },
    infoBadgeText: {
      fontFamily: webFont("Inter"),
      fontSize: 12 * scaleAll,
      color: "#FFFFFF",
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: 0.6,
    },
    detailsWrap: {
      gap: 10 * scaleAll,
    },
    detailCard: {
      borderRadius: 22 * scaleAll,
      backgroundColor: "#FBFBFA",
      borderWidth: 1,
      borderColor: "#ECE8E1",
      paddingHorizontal: 16 * scaleAll,
      paddingTop: 14 * scaleAll,
      paddingBottom: 14 * scaleAll,
      flexDirection: "row",
      alignItems: "flex-start",
      gap: 12 * scaleAll,
    },
    detailCardStack: {
      flexDirection: "column",
      gap: 10 * scaleAll,
    },
    iconWrap: {
      width: 34 * scaleAll,
      height: 34 * scaleAll,
      borderRadius: 999,
      backgroundColor: "#F4F1EB",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    detailBody: {
      flex: 1,
      minWidth: 0,
    },
    detailLabel: {
      fontFamily: webFont("Inter"),
      fontSize: 11 * scaleAll,
      color: "#7A746C",
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: 0.6,
      marginBottom: 4 * scaleAll,
    },
    detailText: {
      fontFamily: webFont("Inter"),
      fontSize: 15 * scaleAll,
      lineHeight: 24 * scaleAll,
      color: "#2F2A25",
      fontWeight: "500",
    },
    detailLink: {
      fontFamily: webFont("Inter"),
      fontSize: 15 * scaleAll,
      lineHeight: 24 * scaleAll,
      color: "#0B5CAD",
      fontWeight: "600",
      textDecorationLine: "underline",
    },
    workTimeWrap: {
      gap: 4 * scaleAll,
    },
    routeCard: {
      marginTop: 14 * scaleAll,
      borderRadius: 24 * scaleAll,
      backgroundColor: "#F5FBF6",
      borderWidth: 1,
      borderColor: "#DDEFE1",
      paddingHorizontal: 18 * scaleAll,
      paddingTop: 18 * scaleAll,
      paddingBottom: 18 * scaleAll,
    },
    routeText: {
      fontFamily: webFont("Inter"),
      fontSize: 14 * scaleAll,
      lineHeight: 21 * scaleAll,
      color: "#435246",
      fontWeight: "500",
      marginBottom: 12 * scaleAll,
    },
    routeButton: {
      minHeight: isSmallVersion ? 50 * scaleAll : 54 * scaleAll,
      borderRadius: 18 * scaleAll,
      backgroundColor: "#111111",
      paddingHorizontal: 18 * scaleAll,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 10 * scaleAll,
    },
    routeButtonText: {
      fontFamily: webFont("Inter"),
      fontSize: 14 * scaleAll,
      color: "#FFFFFF",
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: 0.5,
    },
  });

const openUrl = (url: string) => {
  Linking.openURL(url).catch(() => null);
};

const ContactInfoScreen: React.FC<ContactInfoScreenProps> = ({
  navigation,
  title,
  contact,
  showBrandManager = false,
  eyebrow,
  subtitle,
}) => {
  const { isSmallVersion, isMediumVersion, scaleAll } = useAppContext();
  const styles = React.useMemo(
    () => createStyles(scaleAll, isSmallVersion, isMediumVersion),
    [isMediumVersion, isSmallVersion, scaleAll]
  );
  const mapRef = useRef<ymaps.Map>(null);

  return (
    <View style={styles.screen}>
      <BackButtonBackHome navigation={navigation} />

      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.shell}>
          <View style={styles.hero}>
            {eyebrow ? (
              <Text selectable={false} style={styles.eyebrow}>
                {eyebrow}
              </Text>
            ) : null}
            <Text selectable={false} style={styles.title}>
              {title}
            </Text>
            {subtitle ? (
              <Text selectable={false} style={styles.subtitle}>
                {subtitle}
              </Text>
            ) : null}
          </View>

          <View style={styles.introWrap}>
            <View style={styles.mapCard}>
              <View style={styles.mapInner}>
                <YMapsFullScreen
                  width={"100%"}
                  mapRef_={mapRef}
                  selected={null}
                  dealerList={[contact]}
                  defaultState={{
                    center: contact.coordinates,
                    zoom: 12,
                  }}
                />
              </View>
            </View>

            <View style={styles.infoCard}>
              <View style={styles.infoBadge}>
                <Text selectable={false} style={styles.infoBadgeText}>
                  {contact.name}
                </Text>
              </View>

              <View style={styles.detailsWrap}>
                <View style={styles.detailCard}>
                  <View style={styles.iconWrap}>
                    <MapPin color="#111111" size={18 * scaleAll} />
                  </View>
                  <View style={styles.detailBody}>
                    <Text selectable={false} style={styles.detailLabel}>
                      Адрес
                    </Text>
                    <Text selectable={false} style={styles.detailText}>
                      {contact.address}
                    </Text>
                  </View>
                </View>

                <View style={[styles.detailCard, styles.detailCardStack]}>
                  <View style={{ flexDirection: "row", alignItems: "flex-start", gap: 12 * scaleAll }}>
                    <View style={styles.iconWrap}>
                      <Clock3 color="#111111" size={18 * scaleAll} />
                    </View>
                    <View style={styles.detailBody}>
                      <Text selectable={false} style={styles.detailLabel}>
                        Время работы
                      </Text>
                      <View style={styles.workTimeWrap}>
                        {contact.workTime.map((time, index) => (
                          <Text key={`${time}-${index}`} selectable={false} style={styles.detailText}>
                            {time}
                          </Text>
                        ))}
                      </View>
                    </View>
                  </View>

                  {showBrandManager && contact.brand_manager ? (
                    <View style={{ flexDirection: "row", alignItems: "flex-start", gap: 12 * scaleAll }}>
                      <View style={styles.iconWrap}>
                        <UserRound color="#111111" size={18 * scaleAll} />
                      </View>
                      <View style={styles.detailBody}>
                        <Text selectable={false} style={styles.detailLabel}>
                          Бренд-менеджер
                        </Text>
                        <Text selectable={false} style={styles.detailText}>
                          {contact.brand_manager}
                        </Text>
                      </View>
                    </View>
                  ) : null}
                </View>

                {contact.phoneNumbers.map((phone, index) => (
                  <View key={`phone-${phone}-${index}`} style={styles.detailCard}>
                    <View style={styles.iconWrap}>
                      <Phone color="#111111" size={18 * scaleAll} />
                    </View>
                    <View style={styles.detailBody}>
                      <Text selectable={false} style={styles.detailLabel}>
                        Телефон
                      </Text>
                      <Text style={styles.detailLink} onPress={() => openUrl(`tel:${phone}`)}>
                        {phone}
                      </Text>
                    </View>
                  </View>
                ))}

                {contact.mails.map((mail, index) => (
                  <View key={`mail-${mail}-${index}`} style={styles.detailCard}>
                    <View style={styles.iconWrap}>
                      <Mail color="#111111" size={18 * scaleAll} />
                    </View>
                    <View style={styles.detailBody}>
                      <Text selectable={false} style={styles.detailLabel}>
                        Email
                      </Text>
                      <Text style={styles.detailLink} onPress={() => openUrl(`mailto:${mail}`)}>
                        {mail}
                      </Text>
                    </View>
                  </View>
                ))}

                {contact.webSites.map((site, index) => (
                  <View key={`site-${site}-${index}`} style={styles.detailCard}>
                    <View style={styles.iconWrap}>
                      <Globe2 color="#111111" size={18 * scaleAll} />
                    </View>
                    <View style={styles.detailBody}>
                      <Text selectable={false} style={styles.detailLabel}>
                        Сайт
                      </Text>
                      <Text
                        style={styles.detailLink}
                        onPress={() => openUrl(site.startsWith("http") ? site : `https://${site}`)}
                      >
                        {site.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                      </Text>
                    </View>
                  </View>
                ))}
              </View>

              <View style={styles.routeCard}>
                <Text selectable={false} style={styles.routeText}>
                  Открыть точку на карте и сразу построить маршрут до нужного адреса.
                </Text>

                <PressableWrapp onPress={() => openNavigation(contact.coordinates)} style={styles.routeButton}>
                  <Navigation color="#FFFFFF" size={18 * scaleAll} />
                  <Text selectable={false} style={styles.routeButtonText}>
                    Проложить маршрут
                  </Text>
                </PressableWrapp>
              </View>
            </View>
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

export default React.memo(ContactInfoScreen);
